import * as React from 'react';
import cn from 'classnames';
import { observer } from 'mobx-react';

import { DOMAIN_STRING } from 'shared/entities/domain';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner/LoaderSpinner';
import {
  LoaderSpinnerColor,
  LoaderSpinnerSize
} from 'shared/newEntities/components/LoaderSpinner';
import { UpdateIcon } from 'shared/newComponents/icons';
import { ComponentType } from 'shared/entities/components/Component';
import {
  TranslationString,
  useTypedTranslation
} from 'shared/entities/localization';
import Reference from 'shared/newComponents/Reference';
import { IconSize } from 'shared/entities/components/Icon';
import TypedTransComponent from 'shared/components/TypedTransComponent';
import { ValidatorResult } from 'shared/entities/validator';
import { InputSize } from 'shared/newEntities/components/Input';

import Input from '../Input';
import WithMeta from '../WithMeta';

import './DomainField.modules.scss';

export type DomainFieldProps = {
  value: string;
  onChange: (value: string) => void;
  error?: ValidatorResult;
  onBlur?: () => void;
  label?: TranslationString;
  placeholder?: TranslationString;
  disabledGeneration?: boolean;
  onGenerateDomain: () => void;
  generatingDomainLoading?: boolean;
  showGenerationButton?: boolean;
  disabled?: boolean;
  className?: string;
};

const DomainField: React.FC<DomainFieldProps> = ({
  value,
  onChange,
  error,
  onBlur,
  label,
  placeholder,
  disabledGeneration,
  onGenerateDomain,
  generatingDomainLoading,
  showGenerationButton,
  disabled,
  className
}: DomainFieldProps) => {
  const { t } = useTypedTranslation('components');

  return (
    <WithMeta
      className={className}
      reference={{
        title: t('form.wrapped.domainField.refTitle'),
        description: (
          <TypedTransComponent
            i18nKey="form.wrapped.domainField.ref"
            ns="components"
          >
            1. Только латинские символы, цифры, -, _ <br />
            2. Длина от&nbsp;7&nbsp;до&nbsp;23&nbsp;символов
            <br />
            3. Не&nbsp;должен начинаться и&nbsp;заканчиваться &laquo;-&raquo; и
            &laquo;_&raquo;
            <br />
            4. Не&nbsp;должен начинаться с&nbsp;цифры
          </TypedTransComponent>
        )
      }}
      label={label ?? t('form.wrapped.domainField.label')}
      error={error}
      bottomLabel={t('form.wrapped.domainField.bottomLabel')}
    >
      <div styleName="field-container">
        <Input
          styleName="field-container__input"
          size={InputSize.xl}
          placeholder={
            placeholder ?? t('form.wrapped.domainField.inputPlaceholder')
          }
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          endOfLine={`.${DOMAIN_STRING}`}
          disabled={disabled}
        />
        {showGenerationButton && (
          <div styleName={cn('field-container__right')}>
            {generatingDomainLoading && (
              <LoaderSpinner
                size={LoaderSpinnerSize.lg}
                color={LoaderSpinnerColor.blue}
              />
            )}
            {!generatingDomainLoading && (
              <Reference
                title={(t) =>
                  t('form.wrapped.domainField.regenerate', {
                    ns: 'components'
                  })
                }
                trigger={
                  <div styleName="field-container__icon-wrapper">
                    <UpdateIcon
                      className={cn('hoverable')}
                      styleName={cn(
                        disabledGeneration
                          ? 'field-container__icon_disabled'
                          : 'field-container__icon'
                      )}
                      action={{
                        type: ComponentType.svg,
                        onClick: onGenerateDomain
                      }}
                      iconSize={IconSize.L}
                    />
                  </div>
                }
              />
            )}
          </div>
        )}
      </div>
    </WithMeta>
  );
};

export default observer(DomainField);
